/**
 * DashboardReferralController — rubrique « Parrainage » du dashboard
 * payant §30.E (EPIC-063 / 63.4).
 *
 * Affichage lecture seule du code parrain de l'organisation courante,
 * du lien partageable `https://notamicro.fr/?r=CODE` (avec bouton
 * « Copier le lien ») et de la liste des filleuls (raison sociale +
 * statut lisible + date de rattachement).
 *
 * Le code est `null` côté API quand l'org n'a pas de `referral_code`
 * (compte gratuit, résilié, suspendu) — on bascule alors sur un message
 * d'invitation à activer l'abonnement.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";
import { cloneTemplate, fillSlots } from "/js/utils/template.js";
import { formatDate } from "/js/utils/format.js";

export default class DashboardReferralController extends BaseController {
  static targets = [
    "emptyCode",
    "codeBlock",
    "code",
    "link",
    "copyButton",
    "copyFeedback",
    "emptyFilleuls",
    "filleulsTable",
    "filleulsBody",
  ];

  async connect() {
    try {
      const data = await api.get("/me/referrals");
      this._render(data);
    } catch (error) {
      this.showError(
        error && typeof error.message === "string" && error.message !== ""
          ? error.message
          : "Impossible de charger votre code de parrainage.",
      );
    }
  }

  async copyLink() {
    if (!this.hasLinkTarget) return;
    const value = this.linkTarget.value;
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      if (this.hasCopyFeedbackTarget) {
        this.copyFeedbackTarget.hidden = false;
        setTimeout(() => {
          if (this.hasCopyFeedbackTarget) this.copyFeedbackTarget.hidden = true;
        }, 2000);
      }
    } catch {
      this.linkTarget.select();
    }
  }

  _render(data) {
    if (!data || data.code === null || data.code === undefined) {
      if (this.hasEmptyCodeTarget) this.emptyCodeTarget.hidden = false;
      if (this.hasCodeBlockTarget) this.codeBlockTarget.hidden = true;
      return;
    }

    if (this.hasEmptyCodeTarget) this.emptyCodeTarget.hidden = true;
    if (this.hasCodeBlockTarget) this.codeBlockTarget.hidden = false;

    if (this.hasCodeTarget) this.codeTarget.textContent = data.code;
    if (this.hasLinkTarget) this.linkTarget.value = data.link ?? "";

    this._renderFilleuls(Array.isArray(data.filleuls) ? data.filleuls : []);
  }

  _renderFilleuls(filleuls) {
    if (!this.hasFilleulsBodyTarget) return;

    if (filleuls.length === 0) {
      if (this.hasEmptyFilleulsTarget) this.emptyFilleulsTarget.hidden = false;
      if (this.hasFilleulsTableTarget) this.filleulsTableTarget.hidden = true;
      return;
    }

    if (this.hasEmptyFilleulsTarget) this.emptyFilleulsTarget.hidden = true;
    if (this.hasFilleulsTableTarget) this.filleulsTableTarget.hidden = false;

    this.filleulsBodyTarget.replaceChildren();
    for (const filleul of filleuls) {
      const fragment = cloneTemplate("tpl-referral-filleul-row");
      fillSlots(fragment, {
        name: filleul.commercialName || filleul.legalName || "",
        statusLabel: filleul.statusLabel || "",
        attachedAt: formatDate(filleul.attachedAt),
      });
      this.filleulsBodyTarget.appendChild(fragment);
    }
  }
}
